import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Tooltip,
  alpha,
  Alert,
  InputAdornment,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  QrCode as QrCodeIcon,
  Preview as PreviewIcon,
  Print as PrintIcon,
  Link as LinkIcon,
  Wifi as WifiIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Router as RouterIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

const OpcoesImpressao = ({ 
  configuracoes, 
  handleChange, 
  customTheme, 
  imprimirTeste, 
  imprimirTesteQrCode,
  testeEnviado,
  templateOptions = []
}) => {
  const [testandoConexao, setTestandoConexao] = useState(false);
  const [statusConexao, setStatusConexao] = useState(null); // null, 'success', 'error'
  const [mensagemConexao, setMensagemConexao] = useState('');

  const testarConexaoImpressora = async () => {
    if (!configuracoes.ipImpressora || !configuracoes.ipImpressora.trim()) {
      setStatusConexao('error');
      setMensagemConexao('Por favor, informe o IP da impressora');
      return;
    }

    setTestandoConexao(true);
    setStatusConexao(null);
    setMensagemConexao('');

    try {
      const api = require('../../../services/api').default;
      const response = await api.post('/printer/teste-conexao', {});
      const data = response.data;
       
      if (!data.success) {
        throw new Error(data.message || 'Falha no teste de conexão');
      }

      setStatusConexao('success');
      setMensagemConexao(data.message || 'Conexão com a impressora térmica estabelecida com sucesso!');
    } catch (error) {
      console.error('Erro ao testar conexão:', error);
      setStatusConexao('error');
      setMensagemConexao('Erro de rede ao tentar conectar com a impressora');
    } finally {
      setTestandoConexao(false);
    }
  };

  const handleIpChange = (event) => {
    handleChange(event);
    // Limpar status quando o IP for alterado
    if (statusConexao) {
      setStatusConexao(null);
      setMensagemConexao('');
    }
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 2,
        color: customTheme.palette.primary.main
      }}>
        <PrintIcon sx={{ mr: 1 }} />
        <Typography variant="h6" fontWeight={600}>
          Opções de Impressão
        </Typography>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {/* Configuração IP da Impressora */}
      <Box sx={{ mb: 3, p: 2, bgcolor: alpha(customTheme.palette.primary.main, 0.05), borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <RouterIcon sx={{ mr: 1, color: customTheme.palette.primary.main }} />
          <Typography variant="subtitle1" fontWeight={600} color="primary">
            Conexão com Impressora Térmica
          </Typography>
        </Box>
        
        <TextField
          fullWidth
          label="IP da Impressora Térmica"
          name="ipImpressora"
          value={configuracoes.ipImpressora || ''}
          onChange={handleIpChange}
          variant="outlined"
          size="small"
          placeholder="192.168.1.100"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <WifiIcon fontSize="small" sx={{ opacity: 0.7 }} />
              </InputAdornment>
            ),
            endAdornment: statusConexao && (
              <InputAdornment position="end">
                {statusConexao === 'success' ? (
                  <CheckCircleIcon sx={{ color: 'success.main' }} />
                ) : (
                  <ErrorIcon sx={{ color: 'error.main' }} />
                )}
              </InputAdornment>
            )
          }}
          helperText="Informe o endereço IP da impressora térmica na rede local (ex: 192.168.1.100)"
        />
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={testarConexaoImpressora}
            disabled={testandoConexao || !configuracoes.ipImpressora?.trim()}
            startIcon={testandoConexao ? <CircularProgress size={16} /> : <WifiIcon />}
          >
            {testandoConexao ? 'Testando...' : 'Testar Conexão'}
          </Button>
        </Box>
        
        {statusConexao && (
          <Alert 
            severity={statusConexao} 
            sx={{ mt: 1 }}
            variant="filled"
          >
            {mensagemConexao}
          </Alert>
        )}
      </Box>

      {/* Configuração de Timezone */}
      <Box sx={{ mb: 3, p: 2, bgcolor: alpha(customTheme.palette.warning.main, 0.05), borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ScheduleIcon sx={{ mr: 1, color: customTheme.palette.warning.main }} />
          <Typography variant="subtitle1" fontWeight={600} color="warning.main">
            Configuração de Horário
          </Typography>
        </Box>
        
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Fuso Horário</InputLabel>
          <Select
            name="timezone"
            value={configuracoes.timezone || 'America/Sao_Paulo'}
            onChange={handleChange}
            label="Fuso Horário"
          >
            <MenuItem value="America/Sao_Paulo">Brasil - GMT-3 (America/Sao_Paulo)</MenuItem>
            <MenuItem value="America/Manaus">Manaus - GMT-4 (America/Manaus)</MenuItem>
            <MenuItem value="America/Rio_Branco">Acre - GMT-5 (America/Rio_Branco)</MenuItem>
            <MenuItem value="America/Noronha">Fernando de Noronha - GMT-2 (America/Noronha)</MenuItem>
            <MenuItem value="UTC">UTC - Horário Universal</MenuItem>
          </Select>
        </FormControl>
        
        <Alert severity="info" variant="outlined">
          O horário configurado será usado nos tickets impressos. 
          Recomendamos usar "Brasil - GMT-3" para a maioria das regiões do Brasil.
        </Alert>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 2 
      }}>
        {/* Seletor de Template (JSON em disco) */}
        <Box sx={{ p: 2, bgcolor: alpha(customTheme.palette.success.main, 0.05), borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} color="success.main" sx={{ mb: 1 }}>
            Template de Impressão (JSON)
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Template</InputLabel>
            <Select
              name="templateTicket"
              value={configuracoes.templateTicket || ''}
              label="Template"
              onChange={handleChange}
            >
              <MenuItem value=""><em>Padrão (interno)</em></MenuItem>
              {templateOptions.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Selecione um arquivo .json existente em templates. Para voltar ao layout padrão, escolha "Padrão (interno)".
          </Typography>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={configuracoes.incluirLogo}
              onChange={handleChange}
              name="incluirLogo"
              color="primary"
            />
          }
          label="Incluir Logotipo"
        />
        
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {configuracoes.incluirLogo ? 'O logo será impresso no topo do ticket' : 'O logo não será impresso'}
          </Typography>
          <Tooltip title="Enviar logo">
            <IconButton 
              disabled={!configuracoes.incluirLogo}
              sx={{ opacity: configuracoes.incluirLogo ? 1 : 0.5 }}
            >
              <PhotoCameraIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Divider />
        
        <FormControlLabel
          control={
            <Switch
              checked={configuracoes.incluirQrCode}
              onChange={handleChange}
              name="incluirQrCode"
              color="primary"
            />
          }
          label="Incluir QR Code"
        />
        
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {configuracoes.incluirQrCode ? 'QR Code com o número da senha será impresso' : 'QR Code não será impresso'}
          </Typography>
          <QrCodeIcon 
            fontSize="small" 
            sx={{ 
              opacity: configuracoes.incluirQrCode ? 1 : 0.5,
              color: 'text.secondary'
            }} 
          />
        </Box>

        {configuracoes.incluirQrCode && (
          <Box sx={{ ml: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="URL para o QR Code"
              name="qrCodeUrl"
              value={configuracoes.qrCodeUrl || ''}
              onChange={handleChange}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: <LinkIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />,
              }}
              helperText="URL para onde o cliente será redirecionado ao escanear o QR Code. O número da senha será adicionado como parâmetro."
            />
          </Box>
        )}

        <Divider />
        
        <FormControlLabel
          control={
            <Switch
              checked={configuracoes.incluirData}
              onChange={handleChange}
              name="incluirData"
              color="primary"
            />
          }
          label="Incluir Data"
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={configuracoes.incluirHora}
              onChange={handleChange}
              name="incluirHora"
              color="primary"
            />
          }
          label="Incluir Hora"
        />
      </Box>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<PreviewIcon />}
          onClick={imprimirTeste}
          disabled={testeEnviado}
          sx={{ mr: 1 }}
        >
          {testeEnviado ? 'Enviando...' : 'Imprimir Teste'}
        </Button>
        
        {configuracoes.incluirQrCode && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<QrCodeIcon />}
            onClick={imprimirTesteQrCode}
            disabled={testeEnviado}
          >
            Testar QR Code
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default OpcoesImpressao; 